/**
 * HTML Generator
 *
 * Generates a fully self-contained HTML document from a
 * `MigrationDocument`. The output is the primary "browser-friendly"
 * rendering of the same content that the PDF and DOCX generators
 * produce — every section, sub-section, table, linkedItem, and flow
 * diagram is included.
 *
 * Design choices:
 *  - Self-contained: inline CSS, inline SVG for flow diagrams. No
 *    external requests so the file works offline and can be saved
 *    with browser "Save As".
 *  - Flow diagrams: emitted as inline interactive SVG via
 *    {@link renderFlowDiagramSvg}. Falls back to a `<pre>` block of
 *    `MermaidDiagram.syntax` if no graph is available.
 *  - Anchors: each section's `anchorId` becomes the `id` of the
 *    section heading so the table of contents and `linkedItems`
 *    in-page links resolve.
 *  - Escaping: all model strings flow through {@link escapeHtml}
 *    before reaching the output. We never interpolate raw model
 *    content into attribute values without escaping; numeric
 *    constants and statically-known classnames are the only
 *    unescaped fragments.
 *  - Browser-safe: this module has no `node:*` imports or Node-only
 *    globals so it can run unchanged in the static client bundle.
 */

import type {
  DocumentSection,
  DocumentTable,
  DocumentLinkedItem,
  MigrationDocument,
} from '../types/generator.js';
import type { MermaidDiagram } from '../types/mermaid.js';
import type { GraphNode } from '../types/graph.js';
import { formatTargetPlatformLabel } from '../types/velocity.js';
import type { VelocityIconMap } from '../types/velocity.js';
import { renderFlowDiagramSvg } from './flow-svg-renderer.js';
import { renderVelocityFlowSvg } from './velocity-flow.js';
import { sanitizeVelocityLabel } from '../generator/velocity-label.js';

/** Options that customise the generated HTML guide. */
export interface HtmlGenerateOptions {
  /**
   * Vendored Velocity component icons keyed by Velocity component name.
   * When present, the "Velocity model" diagram view embeds the matching icon
   * in each node; otherwise nodes fall back to a kind-colored glyph.
   */
  velocityIcons?: VelocityIconMap;
}

/**
 * Renders the full migration document as a single self-contained
 * HTML string. The string is UTF-8 text safe to write to a `.html`
 * file or stream as `text/html; charset=utf-8`.
 */
export function generateHtml(doc: MigrationDocument, options: HtmlGenerateOptions = {}): string {
  const headingChunks: string[] = [];
  for (const section of doc.sections) {
    appendSectionHtml(section, 2, headingChunks, options);
  }
  const toc = buildToc(doc.sections);
  const generatedAt = formatGeneratedDate(doc.generatedDate);
  const sourceLine = doc.sourceFileName
    ? `<dt>Source file</dt><dd>${escapeHtml(doc.sourceFileName)}</dd>`
    : '';

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<meta name="generator" content="Velocity Migration Tool">
<title>${escapeHtml(doc.title)}</title>
<style>${INLINE_CSS}</style>
</head>
<body>
<noscript><style>.report__hint,.report__diagram-toolbar,.report__diagram-minimap{display:none!important}.report__section.is-collapsed > *{display:revert!important}</style></noscript>
<main class="report is-view-geoevent" id="report-top">
<div class="report__layout">
  <aside class="report__sidebar" aria-label="Document contents">
    ${toc}
  </aside>
  <div class="report__main">
    <div class="report__toolbar" data-vma-view-toolbar>
      <span class="report__toolbar-label">Show configuration for</span>
      <div class="report__view-switch" role="group" aria-label="Switch between GeoEvent Server and Velocity content">
        <button type="button" data-vma-view-global="geoevent" class="is-active" aria-pressed="true">GeoEvent Server</button>
        <button type="button" data-vma-view-global="velocity" aria-pressed="false">Velocity</button>
      </div>
    </div>
    <header class="report__header">
      <h1>${escapeHtml(doc.title)}</h1>
      <dl class="report__meta">
        <dt>Generated</dt><dd>${escapeHtml(generatedAt)}</dd>
        <dt>Target platform</dt><dd>${escapeHtml(formatTargetPlatformLabel(doc.targetPlatform))}</dd>
        ${sourceLine}
      </dl>
    </header>
    ${renderHintBanner()}
    ${renderBetaNotice()}
    ${headingChunks.join('\n')}
    <footer class="report__footer">
      <p>Generated by the Velocity Migration Tool. This document was rendered entirely client-side from a single response and contains no external links or scripts.</p>
    </footer>
  </div>
</div>
</main>
<script>${INLINE_JS}</script>
</body>
</html>
`;
}

/**
 * Formats a `Date` as a professional, locale-friendly string such as
 * `June 4, 2026, at 10:45 a.m.` (en-US, lowercase day period with
 * periods). Falls back to the ISO string if the date is invalid.
 */
function formatGeneratedDate(date: Date): string {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return String(date);
  }
  const datePart = new Intl.DateTimeFormat('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  }).format(date);
  let hours = date.getHours();
  const minutes = date.getMinutes();
  const period = hours >= 12 ? 'p.m.' : 'a.m.';
  hours = hours % 12;
  if (hours === 0) hours = 12;
  const minutesStr = minutes < 10 ? '0' + minutes : String(minutes);
  return `${datePart}, at ${hours}:${minutesStr} ${period}`;
}

/**
 * Renders the first-run hint banner. Hidden by default via CSS and
 * shown by the inline script when `vma-guide-ui-v1.hintDismissed` is
 * falsy. Hidden in print and when JavaScript is disabled.
 */
function renderHintBanner(): string {
  return `<aside class="report__hint" hidden role="status" aria-live="polite" data-vma-hint>
  <div class="report__hint-body">
    <strong>This guide is interactive.</strong> Use the table of contents to jump between sections, collapse component cards by clicking their heading toggles, and pan / zoom service flow diagrams.
  </div>
  <button type="button" class="report__hint-close" data-vma-hint-close aria-label="Dismiss tip">Got it</button>
</aside>`;
}

/**
 * Renders the beta-release notice. Always visible (not dismissible)
 * and printed with the document so it appears in any saved-as-PDF
 * copies. Plain text only — no interpolation of model fields, so no
 * escaping is needed here.
 */
function renderBetaNotice(): string {
  return `<aside class="report__beta" role="note" aria-label="Beta release notice">
  <strong>Beta release.</strong>
  The ArcGIS Velocity Migration Tool is currently available in beta. While it is designed to support migration workflows, some components or configurations may not translate exactly between systems. We encourage you to review migrated content and provide feedback to help improve compatibility and overall experience. Functionality and behavior may be refined prior to general availability.
</aside>`;
}

/**
 * Walks a section and its subsections, appending one block of HTML
 * per section to `out`. `depth` selects the heading level (clamped
 * to h2..h6 so the page only ever has one h1 — the document title).
 */
function appendSectionHtml(section: DocumentSection, depth: number, out: string[], options: HtmlGenerateOptions = {}): void {
  const level = Math.min(6, Math.max(2, depth));
  const anchorId = resolveAnchorId(section);
  const idAttr = anchorId ? ` id="${escapeAttr(anchorId)}"` : '';
  const metaAttrs = renderMetadataAttrs(section);
  const parts: string[] = [];
  parts.push(`<section class="report__section"${metaAttrs}>`);
  const anchorLink = anchorId
    ? `<a class="report__anchor-link" href="#${escapeAttr(anchorId)}" aria-label="Copy link to ${escapeAttr(section.title)}" tabindex="-1">#</a>`
    : '';
  parts.push(`<h${level}${idAttr}>${escapeHtml(section.title)}${anchorLink}</h${level}>`);
  if (section.content) {
    parts.push(`<div class="report__body">${formatBody(section.content)}</div>`);
  }
  if (section.linkedItems && section.linkedItems.length > 0) {
    parts.push(renderLinkedItems(section.linkedItems));
  }
  if (section.tables) {
    for (const table of section.tables) parts.push(renderTable(table));
  }
  if (section.diagram) {
    parts.push(renderDiagram(section.diagram, anchorId, options));
  }
  parts.push(`</section>`);
  out.push(parts.join('\n'));
  if (section.subsections) {
    for (const sub of section.subsections) appendSectionHtml(sub, depth + 1, out, options);
  }
}

/**
 * Returns the section's explicit `anchorId` when set, otherwise a
 * deterministic slug derived from the title. This keeps existing
 * cross-links stable (they always go through `anchorId`) while
 * giving the TOC something to link to for top-level structural
 * sections that historically had no explicit anchor.
 */
function resolveAnchorId(section: DocumentSection): string | undefined {
  if (section.anchorId) return section.anchorId;
  const slug = section.title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 64);
  return slug ? `sec-${slug}` : undefined;
}

/**
 * Builds the data-* attribute string for a section based on its
 * optional metadata. Emits nothing when metadata is absent so the
 * resulting HTML stays identical to today's output for untagged
 * sections (PDF/DOCX have no behavior change either way since they
 * ignore the field upstream).
 */
function renderMetadataAttrs(section: DocumentSection): string {
  const meta = section.metadata;
  const parts: string[] = [];
  if (meta?.kind) parts.push(`data-kind="${escapeAttr(meta.kind)}"`);
  if (meta?.status) parts.push(`data-status="${escapeAttr(meta.status)}"`);
  if (meta?.componentType) parts.push(`data-type="${escapeAttr(meta.componentType)}"`);
  return parts.length > 0 ? ' ' + parts.join(' ') : '';
}

/**
 * Renders a flat 2-level table of contents from the top-level
 * sections (and their immediate subsections). Deep nesting is
 * intentionally not enumerated in the TOC to keep it scannable;
 * users navigate further via inline links once they jump into a
 * section.
 */
function buildToc(sections: DocumentSection[]): string {
  const items: string[] = [];
  for (const s of sections) {
    const sid = resolveAnchorId(s);
    if (!sid) continue;
    let li = `<li><a href="#${escapeAttr(sid)}">${escapeHtml(s.title)}</a>`;
    if (s.subsections && s.subsections.length > 0) {
      const subItems: string[] = [];
      for (const sub of s.subsections) {
        const subId = resolveAnchorId(sub);
        if (!subId) continue;
        subItems.push(`<li><a href="#${escapeAttr(subId)}">${escapeHtml(sub.title)}</a></li>`);
      }
      if (subItems.length > 0) li += `<ol>${subItems.join('')}</ol>`;
    }
    li += `</li>`;
    items.push(li);
  }
  if (items.length === 0) return '';
  // Prepend a "Top of page" entry so users can jump above the title +
  // beta notice from the sidebar. The first real section ("About This
  // Guide") starts below those, so without this entry the TOC has no
  // way to scroll all the way back up. The target id is on `<main
  // class="report">` itself.
  const topItem = `<li><a href="#report-top">Top of page</a></li>`;
  return `<nav class="report__toc" aria-label="Table of contents"><h2>Contents</h2><ol>${topItem}${items.join('')}</ol></nav>`;
}

function renderLinkedItems(items: DocumentLinkedItem[]): string {
  const lis = items.map((item) => {
    let li = `<li><a href="#${escapeAttr(item.anchorId)}">${escapeHtml(item.label)}</a>`;
    if (item.children && item.children.length > 0) {
      li += renderLinkedItems(item.children);
    }
    li += `</li>`;
    return li;
  });
  return `<ul class="report__linked-items">${lis.join('')}</ul>`;
}

/**
 * Classifies a component table as GeoEvent-side or Velocity-side by its title
 * so the global view toggle can show or hide it. Neutral tables (e.g. Arcade
 * migration notes, summaries) return `undefined` and stay visible in both
 * views.
 */
function classifyTableView(title: string): 'geoevent' | 'velocity' | undefined {
  if (/^GeoEvent Server/.test(title) || title === 'Filter Conditions') return 'geoevent';
  if (/^Velocity /.test(title) || title === 'Additional Velocity Components') return 'velocity';
  return undefined;
}

function renderTable(table: DocumentTable): string {
  const rows = table.rows
    .map(
      (r) =>
        `<tr><th scope="row">${escapeHtml(r.parameter)}</th><td>${formatBody(r.value)}</td></tr>`,
    )
    .join('');
  const caption = table.title ? `<caption>${escapeHtml(table.title)}</caption>` : '';
  const view = classifyTableView(table.title || '');
  const viewAttr = view ? ` data-vma-view="${view}"` : '';
  return `<table class="report__table"${viewAttr}>${caption}<tbody>${rows}</tbody></table>`;
}

/**
 * Renders a diagram for the HTML guide. When the diagram includes a
 * `ServiceGraph`, we emit an inline interactive SVG (pan / zoom /
 * click-to-jump). Otherwise we fall back to a `<pre>` of the Mermaid
 * syntax so a single bad diagram never fails the whole document.
 *
 * The `parentAnchorId` is used to build the per-node `data-anchor`
 * map: for a service's full flow diagram, the parent's component
 * sections already use anchor IDs of the form `comp-<name>`, so we
 * can map graph node IDs (which match the component name) directly.
 */
/** Stable anchor ID for a component card, derived from its graph node ID. */
function componentAnchorId(nodeId: string): string {
  return `comp-${nodeId.replace(/[^a-zA-Z0-9-_]/g, '-').toLowerCase()}`;
}

/** Normalizes a node label for best-effort cross-graph matching. */
function normalizeNodeLabel(label: string): string {
  return label.toLowerCase().replace(/\s+/g, ' ').trim();
}

/**
 * Builds a best-effort anchor map for Velocity flow nodes so clicking a node
 * jumps to the matching component card. Velocity node IDs are Velocity-side
 * identifiers that do not line up with the GeoEvent component IDs the cards are
 * anchored by, so nodes are matched by label.
 *
 * Velocity component labels are the *sanitized* form of their GeoEvent origin
 * label (spaces collapsed to `_`, disallowed characters stripped — see
 * `sanitizeVelocityLabel`), so the GeoEvent cards are indexed under both their
 * raw normalized label and their sanitized normalized label. This lets a
 * Velocity node whose label is e.g. `Determine_DeliveryRisk` match the
 * GeoEvent card titled `Determine DeliveryRisk` (first match wins).
 */
function buildVelocityAnchorMap(
  velocityGraph: { nodes: { id: string; label: string }[] },
  geoEventGraph: { nodes: GraphNode[] },
): Record<string, string> {
  const labelToAnchor = new Map<string, string>();
  for (const node of geoEventGraph.nodes) {
    const anchor = componentAnchorId(node.id);
    const rawKey = normalizeNodeLabel(node.label);
    if (rawKey && !labelToAnchor.has(rawKey)) labelToAnchor.set(rawKey, anchor);
    const sanitizedKey = normalizeNodeLabel(sanitizeVelocityLabel(node.label).sanitized);
    if (sanitizedKey && !labelToAnchor.has(sanitizedKey)) labelToAnchor.set(sanitizedKey, anchor);
  }
  const anchorMap: Record<string, string> = {};
  for (const node of velocityGraph.nodes) {
    const key = normalizeNodeLabel(node.label);
    const anchor = labelToAnchor.get(key) ?? labelToAnchor.get(normalizeNodeLabel(sanitizeVelocityLabel(node.label).sanitized));
    if (anchor) anchorMap[node.id] = anchor;
  }
  return anchorMap;
}

function renderDiagram(diagram: MermaidDiagram, parentAnchorId?: string, options: HtmlGenerateOptions = {}): string {
  // Per-component "neighbor" diagrams highlight a center node; service-level
  // overview diagrams do not. Neighbor diagrams get a single shared title;
  // service diagrams get a view-specific title inside each figure so the
  // heading toggles with the GeoEvent/Velocity content.
  const isNeighbor = Boolean(diagram.highlightNodeId || diagram.velocityHighlightNodeId);
  const sharedTitle = isNeighbor
    ? `<p class="report__diagram-title">Node Neighbor(s)</p>`
    : '';
  const geoTitle = isNeighbor ? '' : `<p class="report__diagram-title">GeoEvent Service Diagram</p>`;
  const velTitle = isNeighbor ? '' : `<p class="report__diagram-title">Real-Time Analytic Diagram</p>`;
  if (diagram.graph) {
    try {
      const anchorMap: Record<string, string> = {};
      for (const node of diagram.graph.nodes) {
        anchorMap[node.id] = componentAnchorId(node.id);
      }
      const rendered = renderFlowDiagramSvg(diagram.graph, {
        highlightNodeId: diagram.highlightNodeId,
        anchorMap,
        idPrefix: parentAnchorId ? `vmaflow-${parentAnchorId}` : undefined,
      });
      if (rendered) {
        const geoFigure = `<div data-vma-view="geoevent">${geoTitle}${renderInteractiveDiagram(rendered.svg, rendered.width, rendered.height, rendered.nodeCount, 'geoevent')}</div>`;
        // When a Velocity-side graph is available, emit a matching Velocity
        // figure tagged for the global GeoEvent/Velocity view toggle. Both
        // figures are rendered into the page; the toolbar toggle (via the
        // `.report.is-view-*` CSS) shows exactly one at a time, so there is no
        // longer a per-diagram switch.
        if (diagram.velocityGraph && diagram.velocityGraph.nodes.length > 0) {
          const manualNodeIds = diagram.velocityManualNodeIds && diagram.velocityManualNodeIds.length > 0
            ? new Set(diagram.velocityManualNodeIds)
            : undefined;
          const velocity = renderVelocityFlowSvg(diagram.velocityGraph, {
            highlightNodeId: diagram.velocityHighlightNodeId,
            manualNodeIds,
            idPrefix: parentAnchorId ? `vmavel-${parentAnchorId}` : undefined,
            iconMap: options.velocityIcons,
            anchorMap: buildVelocityAnchorMap(diagram.velocityGraph, diagram.graph),
          });
          if (velocity) {
            // When some nodes are flagged for manual work, explain the red
            // outline beneath the title so the cue is self-describing.
            const velNote = manualNodeIds
              ? `<p class="report__diagram-note"><span class="report__diagram-note-swatch" aria-hidden="true"></span>Components outlined in red require manual configuration.</p>`
              : '';
            const velFigure = `<div data-vma-view="velocity">${velTitle}${velNote}${renderInteractiveDiagram(velocity.svg, velocity.width, velocity.height, velocity.nodeCount, 'velocity')}</div>`;
            return sharedTitle + velFigure + geoFigure;
          }
        }
        return sharedTitle + geoFigure;
      }
    } catch {
      // fall through to syntax fallback
    }
  }
  return `${sharedTitle}<figure class="report__diagram" data-vma-view="geoevent">${geoTitle}<pre class="report__mermaid">${escapeHtml(diagram.syntax)}</pre></figure>`;
}

/**
 * Wraps an SVG fragment in the interactive figure shell (toolbar,
 * minimap container). Pan/zoom JavaScript wires itself to the
 * `data-vma-diagram` attribute on initial load. `variant` selects the
 * legend and styling appropriate to the GeoEvent or Velocity diagram.
 */
function renderInteractiveDiagram(
  svg: string,
  width: number,
  height: number,
  nodeCount: number,
  variant: 'geoevent' | 'velocity' = 'geoevent',
): string {
  const showMinimap = nodeCount >= 8;
  const minimap = showMinimap
    ? `<div class="report__diagram-minimap" data-vma-minimap aria-hidden="true"></div>`
    : '';
  // Velocity nodes don't use colour/shape to signify component type, so a
  // legend would be meaningless; only the GeoEvent view gets one.
  const legend = variant === 'velocity' ? '' : renderGeoEventLegend();
  return `<figure class="report__diagram report__diagram--interactive report__diagram--${variant}" data-vma-diagram data-vma-width="${width}" data-vma-height="${height}">
  <div class="report__diagram-toolbar" data-vma-diagram-toolbar>
    <button type="button" data-vma-diagram-action="fit" title="Fit to view (0)">Fit</button>
    <button type="button" data-vma-diagram-action="reset" title="Reset to 100%">100%</button>
    <button type="button" data-vma-diagram-action="zoom-in" title="Zoom in (+)" aria-label="Zoom in">+</button>
    <button type="button" data-vma-diagram-action="zoom-out" title="Zoom out (\u2212)" aria-label="Zoom out">\u2212</button>
    <button type="button" data-vma-diagram-action="fullscreen" title="Full screen">Full screen</button>
    <span class="report__diagram-zoom" data-vma-diagram-zoom>100%</span>
  </div>
  <div class="report__diagram-canvas" data-vma-diagram-canvas tabindex="0" role="application" aria-label="Pan and zoom flow diagram">${svg}</div>
  ${legend}
  ${minimap}
</figure>`;
}

/** Legend for the GeoEvent (source) diagram view. */
function renderGeoEventLegend(): string {
  return `<div class="report__diagram-legend" aria-hidden="true">
    <span class="report__diagram-legend-item"><span class="report__diagram-legend-swatch report__diagram-legend-swatch--input"></span>Input</span>
    <span class="report__diagram-legend-item"><span class="report__diagram-legend-swatch report__diagram-legend-swatch--processor"></span>Processor</span>
    <span class="report__diagram-legend-item"><span class="report__diagram-legend-swatch report__diagram-legend-swatch--filter"></span>Filter</span>
    <span class="report__diagram-legend-item"><span class="report__diagram-legend-swatch report__diagram-legend-swatch--output"></span>Output</span>
  </div>`;
}

/**
 * Renders body text by escaping HTML and preserving paragraph
 * breaks (blank lines) and single-line breaks. The input is plain
 * text; no markdown is interpreted. Bare `http(s)://` URLs are
 * auto-linkified after escaping; the resulting anchors open in a
 * new tab with `rel="noopener noreferrer"` so external help-topic
 * links work in the rendered HTML the same way they do in the PDF.
 */
function formatBody(text: string): string {
  // Split on blank lines into paragraphs; preserve single newlines as <br>.
  const paragraphs = text.split(/\n{2,}/);
  return paragraphs
    .map((p) => `<p>${linkifyEscaped(escapeHtml(p)).replace(/\n/g, '<br>')}</p>`)
    .join('');
}

/**
 * Wraps bare `http(s)://` URLs in an already-HTML-escaped string with
 * `<a target="_blank" rel="noopener noreferrer">` anchors. Trailing
 * sentence punctuation (`.,;:!?)]'"`) is excluded from the URL so it
 * stays in the surrounding prose.
 *
 * Also recognises a single in-document anchor form, written by
 * `document-builder` as `[label](#anchor-id)`, and rewrites it to an
 * `<a href="#anchor-id">label</a>`. This is deliberately limited to
 * targets that start with `#` (no scheme, no path) so it can never
 * become a vector for arbitrary URLs sneaking into the rendered HTML.
 * The label is plain text only (no nested brackets/parens) and the
 * id matches `sec-` / `comp-` / etc. anchor slugs.
 */
function linkifyEscaped(escaped: string): string {
  // In-document anchor links first so the outer `[...](#...)` brackets
  // are not mistaken for trailing punctuation by the URL pass below.
  let out = escaped.replace(
    /\[([^\[\]\n]+?)\]\(#([A-Za-z][A-Za-z0-9_\-]*)\)/g,
    (_m, label: string, anchor: string) =>
      `<a href="#${anchor}">${label}</a>`,
  );
  out = out.replace(/https?:\/\/[^\s<]+/g, (match) => {
    let url = match;
    let trail = '';
    while (/[.,;:!?)\]'"]$/.test(url)) {
      trail = url.slice(-1) + trail;
      url = url.slice(0, -1);
    }
    if (!url) return match;
    return `<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>${trail}`;
  });
  return out;
}

/**
 * HTML-escapes a string for safe insertion as element content.
 * Also escapes `'` so the same helper is safe in attribute context
 * when the attribute is single-quoted; we use {@link escapeAttr} for
 * the (double-quoted) attribute variant for clarity.
 */
function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function escapeAttr(s: string): string {
  return escapeHtml(s);
}

/**
 * Inline CSS — kept compact but readable. Mirrors the PDF style
 * choices (serif-free, generous whitespace, table zebra-striping).
 */
export const INLINE_CSS = `
:root {
  color-scheme: light;
  --fg: #1f2933;
  --muted: #5b6770;
  --accent: #0066b3;
  --border: #d8dee5;
  --table-stripe: #f7f9fb;
  --warn-bg: #fff8e1;
  --warn-border: #f0c419;
  --max-width: 80rem;
}
* { box-sizing: border-box; }
html, body { margin: 0; padding: 0; background: #fff; color: var(--fg); font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Arial, Helvetica, sans-serif; line-height: 1.5; }
.report { max-width: min(var(--max-width), 98vw); margin: 0 auto; padding: 0 clamp(0.5rem, 1.5vw, 1rem); }
.report__layout { display: grid; grid-template-columns: 17rem minmax(0, 1fr); gap: 1.5rem; align-items: start; }
.report__sidebar { position: sticky; top: 0; align-self: start; max-height: 100vh; overflow-y: auto; padding: 1.5rem 0.75rem 2rem 0.25rem; border-right: 1px solid var(--border); font-size: 0.9rem; }
.report__main { min-width: 0; padding: 2rem 0 4rem; }
@media (max-width: 900px) {
  .report__layout { grid-template-columns: 1fr; gap: 0; }
  .report__sidebar { position: static; max-height: none; overflow: visible; border-right: 0; border-bottom: 1px solid var(--border); padding: 1rem 0; }
  .report__main { padding-top: 1rem; }
}
.report__header { border-bottom: 2px solid var(--border); padding-bottom: 1rem; margin-bottom: 2rem; }
.report__header h1 { font-size: 1.75rem; margin: 0 0 1rem; color: var(--accent); }
.report__meta { display: grid; grid-template-columns: auto 1fr; gap: 0.25rem 1rem; margin: 0; font-size: 0.9rem; color: var(--muted); }
.report__meta dt { font-weight: 600; }
.report__meta dd { margin: 0; }
.report__toc { padding: 0; margin: 0; }
.report__toc h2 { font-size: 0.72rem; margin: 0 0 0.6rem; text-transform: uppercase; letter-spacing: 0.05em; color: var(--muted); font-weight: 600; }
.report__toc ol { list-style: none; margin: 0 0 0.75rem; padding: 0; }
.report__toc > ol > li > a { display: block; padding: 0.3rem 0.55rem; margin: 0.1rem 0; border-radius: 0.25rem; color: var(--fg); text-decoration: none; font-weight: 500; }
.report__toc > ol > li > a:hover, .report__toc > ol > li > a:focus-visible { background: var(--table-stripe); color: var(--accent); }
.report__toc ol ol { padding-left: 0.6rem; margin: 0 0 0.4rem; border-left: 2px solid var(--border); }
.report__toc ol ol a { display: block; padding: 0.2rem 0.5rem; font-size: 0.85rem; color: var(--muted); text-decoration: none; border-radius: 0.2rem; }
.report__toc ol ol a:hover, .report__toc ol ol a:focus-visible { color: var(--accent); background: var(--table-stripe); }
.report__toc a.is-current { color: var(--accent); font-weight: 600; }
.report__section { margin: 2rem 0; }
.report__section h2, .report__section h3, .report__section h4, .report__section h5, .report__section h6 { scroll-margin-top: 4.5rem; }
.report__section h2 { font-size: 1.4rem; color: var(--accent); border-bottom: 1px solid var(--border); padding-bottom: 0.25rem; }
.report__section h3 { font-size: 1.15rem; margin-top: 1.5rem; }
.report__section h4 { font-size: 1rem; margin-top: 1.25rem; color: var(--muted); }
.report__section h5, .report__section h6 { font-size: 0.95rem; margin-top: 1rem; color: var(--muted); }
.report__toggle { margin-left: 0.4em; background: transparent; border: 0; color: var(--muted); cursor: pointer; font-size: 0.85em; }
.report__body p { margin: 0.5rem 0 1rem; }
.report__linked-items { margin: 0.5rem 0 1rem; padding-left: 1.25rem; }
.report__linked-items a { color: var(--accent); text-decoration: none; }
.report__linked-items a:hover { text-decoration: underline; }
.report__table { width: 100%; border-collapse: collapse; margin: 1rem 0 1.5rem; font-size: 0.9rem; }
.report__table caption { caption-side: top; text-align: left; font-weight: 600; padding: 0.25rem 0 0.5rem; }
.report__table th, .report__table td { text-align: left; padding: 0.5rem 0.75rem; border: 1px solid var(--border); vertical-align: top; }
.report__table th[scope="row"] { background: var(--table-stripe); width: 30%; font-weight: 600; }
.report__table tr:nth-child(even) td { background: var(--table-stripe); }
.report__diagram { margin: 1rem 0 1.5rem; padding: 0.5rem; border: 1px solid var(--border); border-radius: 4px; background: #fafbfc; text-align: center; overflow-x: auto; }
.report__diagram-title { margin: 1rem 0 0.4rem; font-size: 0.95rem; font-weight: 600; color: var(--fg); }
.report__diagram-note { margin: 0 0 0.5rem; font-size: 0.8rem; color: var(--muted); display: flex; align-items: center; gap: 0.4rem; }
.report__diagram-note-swatch { display: inline-block; width: 16px; height: 12px; border: 2px solid #DC143C; border-radius: 3px; flex: none; }
.report__diagram img { max-width: 100%; height: auto; }
.report__mermaid { text-align: left; font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace; font-size: 0.8rem; padding: 0.75rem; background: #f0f3f7; border-radius: 4px; overflow-x: auto; }
.report__footer { margin-top: 3rem; padding-top: 1rem; border-top: 1px solid var(--border); font-size: 0.8rem; color: var(--muted); }

/* --- Interactive toolbar, hint banner, anchor links --- */
.report__hint { display: flex; gap: 0.75rem; align-items: flex-start; background: #eef6fb; border: 1px solid #b6dbef; border-radius: 6px; padding: 0.75rem 1rem; margin: 0 0 1rem; }
.report__hint[hidden] { display: none !important; }
.report__hint-body { flex: 1; font-size: 0.9rem; color: var(--fg); }
.report__hint-body kbd { font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace; background: #fff; border: 1px solid var(--border); border-bottom-width: 2px; border-radius: 3px; padding: 0 0.35em; font-size: 0.85em; }
.report__hint-close { background: transparent; border: 1px solid #b6dbef; border-radius: 4px; padding: 0.25rem 0.6rem; font: inherit; cursor: pointer; color: var(--accent); }
.report__hint-close:hover { background: #d6ebf7; }
.report__beta { background: #fff8e8; border-left: 0.25rem solid #8a5200; color: #3d2a00; padding: 0.75rem 1rem; margin: 0 0 1.5rem; font-size: 0.9rem; }
.report__beta strong { display: block; margin-bottom: 0.2rem; }
.report__anchor-link { margin-left: 0.4em; color: var(--muted); text-decoration: none; opacity: 0; font-weight: 400; font-size: 0.8em; }
.report__section h2:hover .report__anchor-link, .report__section h3:hover .report__anchor-link, .report__section h4:hover .report__anchor-link, .report__section h5:hover .report__anchor-link, .report__section h6:hover .report__anchor-link { opacity: 1; }
.report__anchor-link:focus { opacity: 1; outline: 1px dotted var(--muted); }

/* --- Section collapse state --- */
.report__section.is-collapsed > :not(h2):not(h3):not(h4):not(h5):not(h6) { display: none; }
.report__section.is-collapsed > h2, .report__section.is-collapsed > h3, .report__section.is-collapsed > h4, .report__section.is-collapsed > h5, .report__section.is-collapsed > h6 { margin-bottom: 0; }
.report__section[data-status="mapped"] > h2::after, .report__section[data-status="mapped"] > h3::after, .report__section[data-status="mapped"] > h4::after { content: "Mapped"; background: #e8f5e9; color: #2e7d32; }
.report__section[data-status="partial"] > h2::after, .report__section[data-status="partial"] > h3::after, .report__section[data-status="partial"] > h4::after { content: "Partial"; background: #fff3e0; color: #ed6c02; }
.report__section[data-status="unmapped"] > h2::after, .report__section[data-status="unmapped"] > h3::after, .report__section[data-status="unmapped"] > h4::after { content: "Unmapped"; background: #ffebee; color: #c62828; }
.report__section[data-status="manual-required"] > h2::after, .report__section[data-status="manual-required"] > h3::after, .report__section[data-status="manual-required"] > h4::after { content: "Manual"; background: #ffebee; color: #c62828; }
.report__section[data-status] > h2::after, .report__section[data-status] > h3::after, .report__section[data-status] > h4::after { display: inline-block; margin-left: 0.5em; padding: 0 0.5em; font-size: 0.7em; font-weight: 600; vertical-align: middle; border-radius: 999px; }
.report__section.is-jump-target { animation: vma-pulse 1.2s ease-out 1; }
@keyframes vma-pulse { 0% { background: #fff59d; } 100% { background: transparent; } }

/* --- Interactive diagram --- */
.report__diagram--interactive { padding: 0; text-align: left; background: #fafbfc; position: relative; }
.report__diagram-toolbar { display: flex; gap: 0.25rem; align-items: center; padding: 0.4rem 0.5rem; background: transparent; flex-wrap: wrap; position: relative; z-index: 2; }
.report__diagram-toolbar button { background: rgba(255,255,255,0.85); border: 1px solid var(--border); border-radius: 3px; padding: 0.2rem 0.55rem; font: inherit; font-size: 0.8rem; cursor: pointer; }
.report__diagram-toolbar button:hover { background: #fff; }
.report__diagram-zoom { margin-left: auto; font-size: 0.75rem; color: var(--muted); font-variant-numeric: tabular-nums; }
.report__diagram-canvas { position: relative; height: 420px; overflow: hidden; outline: none; cursor: grab; user-select: none; background: #fafbfc; }
.report__diagram-canvas.is-grabbing { cursor: grabbing; }
.report__diagram-canvas:focus-visible { box-shadow: inset 0 0 0 2px var(--accent); }
.report__diagram-canvas svg.vma-flow__svg { width: 100%; height: 100%; display: block; }
.report__diagram-canvas svg.vma-vel__svg { width: 100%; height: 100%; display: block; }
.vma-flow__node { cursor: default; }
.vma-flow__node.is-clickable { cursor: pointer; }
.vma-flow__node.is-clickable:hover rect, .vma-flow__node.is-clickable:hover polygon { filter: brightness(1.05); }
.vma-flow__node:focus-visible rect, .vma-flow__node:focus-visible polygon { stroke: var(--accent); stroke-width: 3; }
.vma-vel__node { cursor: default; }
.vma-vel__node.is-clickable { cursor: pointer; }
.vma-vel__node.is-clickable:hover rect { filter: brightness(0.99); stroke: #07CDCA; }
.vma-vel__node:focus-visible rect { stroke: #007472; stroke-width: 2.5; }
/* --- Global GeoEvent / Velocity view toggle --- */
.report__toolbar { position: sticky; top: 0; z-index: 20; display: flex; gap: 0.6rem; align-items: center; flex-wrap: wrap; background: rgba(255,255,255,0.96); border-bottom: 1px solid var(--border); padding: 0.5rem 0.25rem; margin: 0 0 1rem; }
.report__toolbar-label { font-size: 0.8rem; font-weight: 600; color: var(--muted); }
.report__view-switch { display: inline-flex; border: 1px solid var(--border); border-radius: 6px; overflow: hidden; background: #fff; }
.report__view-switch button { background: #fff; border: 0; border-right: 1px solid var(--border); padding: 0.35rem 0.9rem; font: inherit; font-size: 0.85rem; cursor: pointer; color: var(--muted); }
.report__view-switch button:last-child { border-right: 0; }
.report__view-switch button.is-active { background: #007472; color: #fff; font-weight: 600; }
.report__view-switch button:not(.is-active):hover { background: var(--table-stripe); color: var(--fg); }
.report.is-view-geoevent [data-vma-view="velocity"] { display: none !important; }
.report.is-view-velocity [data-vma-view="geoevent"] { display: none !important; }

.report__diagram-legend { position: absolute; left: 10px; bottom: 10px; display: flex; flex-wrap: wrap; gap: 0.5rem 0.85rem; padding: 0.35rem 0.55rem; background: rgba(255,255,255,0.92); border: 1px solid var(--border); border-radius: 4px; font-size: 0.7rem; color: #333; line-height: 1; z-index: 3; pointer-events: none; }
.report__diagram-legend-item { display: inline-flex; align-items: center; gap: 0.35rem; }
.report__diagram-legend-swatch { display: inline-block; width: 11px; height: 11px; border: 1px solid #999; }
.report__diagram-legend-swatch--input { background: #90EE90; border-color: #5CBF5C; }
.report__diagram-legend-swatch--output { background: #87CEEB; border-color: #5AAFCF; }
.report__diagram-legend-swatch--processor { background: #FFED6F; border-color: #D4C44A; }
.report__diagram-legend-swatch--filter { background: #FFED6F; border-color: #D4C44A; transform: rotate(45deg); }
.report__diagram-minimap { position: absolute; right: 8px; bottom: 8px; width: 160px; height: 100px; background: rgba(255,255,255,0.92); border: 1px solid var(--border); border-radius: 3px; overflow: hidden; z-index: 3; }
.report__diagram-minimap svg { width: 100%; height: 100%; display: block; }
.report__diagram-minimap rect.vma-flow__minimap-viewport { fill: rgba(0,102,179,0.15); stroke: var(--accent); stroke-width: 1; cursor: pointer; }
.report__diagram-canvas:fullscreen { height: 100vh; background: #fafbfc; }
.report__diagram-canvas:-webkit-full-screen { height: 100vh; background: #fafbfc; }

@media print {
  .report__layout { display: block; }
  .report__sidebar { display: none !important; }
  .report__main { padding: 0; }
  .report__toc { page-break-after: always; }
  .report__section { page-break-inside: avoid; }
  .report__toolbar, .report__hint, .report__diagram-toolbar, .report__diagram-minimap, .report__anchor-link { display: none !important; }
  .report__section.is-collapsed > * { display: revert !important; }
  .report__section[data-status] > h2::after, .report__section[data-status] > h3::after, .report__section[data-status] > h4::after { display: inline-block !important; }
  .report__diagram-canvas { height: auto !important; overflow: visible !important; }
  .report__diagram-canvas svg.vma-flow__svg { height: auto !important; }
  .report__diagram-canvas svg.vma-vel__svg { height: auto !important; }
  .report [data-vma-view] { display: revert !important; }
}
`;

/**
 * Inline JavaScript that powers the hint banner, per-section
 * collapse persistence, and per-diagram pan / zoom / click-to-jump /
 * full-screen / mini-map. Wrapped in an IIFE so it never leaks
 * globals. Vanilla; no dependencies.
 */
export const INLINE_JS = `(function(){
  if (typeof document === 'undefined') return;
  var STORAGE_KEY = 'vma-guide-ui-v1';
  var state = { collapsed: [], hintDismissed: false, view: 'geoevent' };
  try {
    var raw = window.localStorage && window.localStorage.getItem(STORAGE_KEY);
    if (raw) state = Object.assign(state, JSON.parse(raw));
  } catch (e) { /* ignore */ }
  // The global "Show configuration for" toggle always defaults to GeoEvent
  // Server on load; the chosen view is intentionally not restored from a prior
  // session (only collapse + hint state persist).
  state.view = 'geoevent';
  function persist() {
    try { window.localStorage && window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch(e) {}
  }
  var saveTimer = null;
  function persistDebounced() {
    if (saveTimer) clearTimeout(saveTimer);
    saveTimer = setTimeout(persist, 150);
  }

  document.addEventListener('DOMContentLoaded', init);
  if (document.readyState === 'interactive' || document.readyState === 'complete') {
    // Script ran after DOMContentLoaded — schedule init() now so handlers attach.
    setTimeout(init, 0);
  }

  var initialised = false;
  function init() {
    if (initialised) return;
    initialised = true;
    var hint = document.querySelector('[data-vma-hint]');
    if (hint && !state.hintDismissed) hint.hidden = false;

    // Single document-level click delegate covers the hint dismiss button
    // and per-section collapse toggles.
    document.addEventListener('click', function(e){
      var t = e.target;
      if (!t || !t.closest) return;
      if (t.closest('[data-vma-hint-close]')) {
        if (hint) hint.hidden = true;
        state.hintDismissed = true; persist();
        return;
      }
      var toggleBtn = t.closest('.report__toggle');
      if (toggleBtn) {
        var sec = toggleBtn.closest('section.report__section');
        if (sec) toggleCollapse(sec, toggleBtn);
        return;
      }
      // The global toolbar switch drives the single shared view state, so
      // GeoEvent/Velocity content stays in sync everywhere in the document.
      var viewBtn = t.closest('[data-vma-view-global]');
      if (viewBtn) {
        var nextView = viewBtn.getAttribute('data-vma-view-global');
        applyView(nextView);
        return;
      }
    });

    document.addEventListener('keydown', function(e){
      if (e.key === 'Escape' && hint && !hint.hidden) { hint.hidden = true; state.hintDismissed = true; persist(); }
    });

    var sections = Array.prototype.slice.call(document.querySelectorAll('section.report__section'));
    // Collapsibility: only component-kind sections get a toggle button + persisted collapse.
    sections.forEach(function(sec){
      if (sec.getAttribute('data-kind') === 'component') {
        var heading = sec.querySelector('h2, h3, h4, h5, h6');
        if (heading) {
          var btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'report__toggle';
          btn.setAttribute('aria-label', 'Toggle section');
          btn.textContent = '\u25BE';
          heading.appendChild(btn);
          if (sec.id && state.collapsed.indexOf(sec.id) !== -1) {
            sec.classList.add('is-collapsed');
            btn.textContent = '\u25B8';
          }
        }
      }
    });

    // Initialise pan/zoom on each interactive diagram
    Array.prototype.forEach.call(document.querySelectorAll('[data-vma-diagram]'), initDiagram);
    // Apply the persisted (or default GeoEvent) view across the document:
    // toggles the .report view class for tables and syncs every diagram pane.
    applyView(state.view);
  }

  // -------------------- Global GeoEvent / Velocity view --------------------
  // A single shared view state drives the sticky toolbar switch. Tables and
  // diagram figures tagged data-vma-view are shown/hidden purely via the
  // .report view class (CSS); there is no per-diagram switch.
  function applyView(view) {
    if (view !== 'geoevent' && view !== 'velocity') view = 'geoevent';
    state.view = view;
    var report = document.querySelector('.report');
    if (report) {
      report.classList.remove('is-view-geoevent', 'is-view-velocity');
      report.classList.add('is-view-' + view);
    }
    Array.prototype.forEach.call(document.querySelectorAll('[data-vma-view-global]'), function(b){
      var on = b.getAttribute('data-vma-view-global') === view;
      if (on) b.classList.add('is-active'); else b.classList.remove('is-active');
      b.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
    persistDebounced();
  }

  function toggleCollapse(sec, btn) {
    var isOpen = !sec.classList.contains('is-collapsed');
    sec.classList.toggle('is-collapsed');
    btn.textContent = isOpen ? '\u25B8' : '\u25BE';
    if (sec.id) {
      var i = state.collapsed.indexOf(sec.id);
      if (isOpen && i === -1) state.collapsed.push(sec.id);
      else if (!isOpen && i !== -1) state.collapsed.splice(i, 1);
      persistDebounced();
    }
  }

  // -------------------- Pan / zoom / full-screen --------------------
  function initDiagram(figure) {
    var canvas = figure.querySelector('[data-vma-diagram-canvas]');
    var svg = canvas && canvas.querySelector('svg.vma-flow__svg, svg.vma-vel__svg');
    var viewport = svg && svg.querySelector('.vma-flow__viewport, .vma-vel__viewport');
    if (!canvas || !svg || !viewport) return;
    var zoomEl = figure.querySelector('[data-vma-diagram-zoom]');
    var contentW = parseFloat(figure.getAttribute('data-vma-width')) || 100;
    var contentH = parseFloat(figure.getAttribute('data-vma-height')) || 100;
    var state2 = { scale: 1, tx: 0, ty: 0 };
    var MIN = 0.25, MAX = 4;

    function apply() {
      viewport.setAttribute('transform', 'translate(' + state2.tx.toFixed(2) + ',' + state2.ty.toFixed(2) + ') scale(' + state2.scale.toFixed(3) + ')');
      if (zoomEl) zoomEl.textContent = Math.round(state2.scale * 100) + '%';
      updateMinimap();
    }
    function rectSize() { var r = canvas.getBoundingClientRect(); return { w: r.width, h: r.height }; }
    function fit() {
      var r = rectSize();
      var sx = r.w / contentW, sy = r.h / contentH;
      state2.scale = Math.max(MIN, Math.min(MAX, Math.min(sx, sy) * 0.95));
      state2.tx = (r.w - contentW * state2.scale) / 2;
      state2.ty = (r.h - contentH * state2.scale) / 2;
      apply();
    }
    function reset() { state2.scale = 1; state2.tx = 0; state2.ty = 0; apply(); }
    function zoomBy(factor, cx, cy) {
      var r = rectSize();
      if (cx == null) cx = r.w / 2;
      if (cy == null) cy = r.h / 2;
      var newScale = Math.max(MIN, Math.min(MAX, state2.scale * factor));
      var k = newScale / state2.scale;
      state2.tx = cx - (cx - state2.tx) * k;
      state2.ty = cy - (cy - state2.ty) * k;
      state2.scale = newScale;
      apply();
    }

    // Toolbar buttons
    Array.prototype.forEach.call(figure.querySelectorAll('[data-vma-diagram-action]'), function(btn){
      btn.addEventListener('click', function(){
        var a = btn.getAttribute('data-vma-diagram-action');
        if (a === 'fit') fit();
        else if (a === 'reset') reset();
        else if (a === 'zoom-in') zoomBy(1.25);
        else if (a === 'zoom-out') zoomBy(1/1.25);
        else if (a === 'fullscreen') toggleFullscreen();
      });
    });
    function toggleFullscreen() {
      if (document.fullscreenElement === canvas || document.webkitFullscreenElement === canvas) {
        if (document.exitFullscreen) document.exitFullscreen();
        else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
      } else {
        if (canvas.requestFullscreen) canvas.requestFullscreen();
        else if (canvas.webkitRequestFullscreen) canvas.webkitRequestFullscreen();
      }
    }
    function onFsChange(){ requestAnimationFrame(fit); }
    document.addEventListener('fullscreenchange', onFsChange);
    document.addEventListener('webkitfullscreenchange', onFsChange);

    // Wheel zoom (cursor-centered)
    canvas.addEventListener('wheel', function(e){
      e.preventDefault();
      var r = canvas.getBoundingClientRect();
      var factor = Math.pow(1.0015, -e.deltaY);
      zoomBy(factor, e.clientX - r.left, e.clientY - r.top);
    }, { passive: false });

    // Pointer drag (pan) — clicks on nodes don't start a drag
    var dragging = false; var sx = 0; var sy = 0; var startTx = 0; var startTy = 0; var pointerId = null;
    canvas.addEventListener('pointerdown', function(e){
      var nodeG = e.target && e.target.closest && e.target.closest('.vma-flow__node, .vma-vel__node');
      if (nodeG) return; // let click handler take it
      dragging = true; pointerId = e.pointerId;
      canvas.setPointerCapture(e.pointerId);
      canvas.classList.add('is-grabbing');
      sx = e.clientX; sy = e.clientY; startTx = state2.tx; startTy = state2.ty;
    });
    canvas.addEventListener('pointermove', function(e){
      if (!dragging || e.pointerId !== pointerId) return;
      state2.tx = startTx + (e.clientX - sx);
      state2.ty = startTy + (e.clientY - sy);
      apply();
    });
    function endDrag(e){
      if (!dragging) return;
      dragging = false; canvas.classList.remove('is-grabbing');
      try { canvas.releasePointerCapture(pointerId); } catch(_) {}
    }
    canvas.addEventListener('pointerup', endDrag);
    canvas.addEventListener('pointercancel', endDrag);

    // Node click → jump to component card; double-click → zoom to neighbors
    var lastClick = 0;
    canvas.addEventListener('click', function(e){
      var g = e.target && e.target.closest && e.target.closest('.vma-flow__node, .vma-vel__node');
      if (!g) return;
      var anchor = g.getAttribute('data-anchor');
      if (!anchor) return;
      var now = Date.now();
      if (now - lastClick < 320) { zoomToNode(g); lastClick = 0; return; }
      lastClick = now;
      setTimeout(function(){
        if (Date.now() - lastClick < 300) return; // suppressed by double-click
        jumpTo(anchor);
      }, 320);
    });
    function jumpTo(anchorId) {
      var el = document.getElementById(anchorId);
      if (!el) return;
      el.classList.remove('is-jump-target');
      // force reflow so animation re-triggers
      void el.offsetWidth;
      el.classList.add('is-jump-target');
      // Compute live offset from the sticky toolbar so the heading
      // lands just below it instead of underneath.
      var toolbar = document.querySelector('.report__toolbar');
      var offset = toolbar ? toolbar.getBoundingClientRect().height + 12 : 0;
      var top = el.getBoundingClientRect().top + window.pageYOffset - offset;
      window.scrollTo({ top: top, behavior: 'smooth' });
    }
    function zoomToNode(g) {
      var bbox; try { bbox = g.getBBox(); } catch(_) { return; }
      var pad = 40;
      var r = rectSize();
      var sxn = r.w / (bbox.width + pad * 2), syn = r.h / (bbox.height + pad * 2);
      var ns = Math.max(MIN, Math.min(MAX, Math.min(sxn, syn)));
      state2.scale = ns;
      state2.tx = (r.w / 2) - (bbox.x + bbox.width / 2) * ns;
      state2.ty = (r.h / 2) - (bbox.y + bbox.height / 2) * ns;
      apply();
    }

    // Keyboard on focused canvas
    canvas.addEventListener('keydown', function(e){
      if (e.key === '+' || e.key === '=') { zoomBy(1.25); e.preventDefault(); }
      else if (e.key === '-' || e.key === '_') { zoomBy(1/1.25); e.preventDefault(); }
      else if (e.key === '0') { fit(); e.preventDefault(); }
      else if (e.key === 'ArrowUp') { state2.ty += 30; apply(); e.preventDefault(); }
      else if (e.key === 'ArrowDown') { state2.ty -= 30; apply(); e.preventDefault(); }
      else if (e.key === 'ArrowLeft') { state2.tx += 30; apply(); e.preventDefault(); }
      else if (e.key === 'ArrowRight') { state2.tx -= 30; apply(); e.preventDefault(); }
      else if (e.key === 'Enter') {
        var n = document.activeElement && document.activeElement.closest && document.activeElement.closest('.vma-flow__node, .vma-vel__node');
        if (n) { var a = n.getAttribute('data-anchor'); if (a) jumpTo(a); }
      }
    });

    // Mini-map
    var minimap = figure.querySelector('[data-vma-minimap]');
    var minimapSvg = null, viewportRect = null;
    if (minimap) {
      minimap.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ' + contentW + ' ' + contentH + '" preserveAspectRatio="xMidYMid meet"></svg>';
      minimapSvg = minimap.firstChild;
      // Clone nodes (very small representation, no labels)
      var clone = viewport.cloneNode(true);
      // Strip text + interactive bits for tiny rendering
      Array.prototype.forEach.call(clone.querySelectorAll('text'), function(t){ t.remove(); });
      Array.prototype.forEach.call(clone.querySelectorAll('.vma-flow__node-highlight, .vma-vel__node-highlight'), function(n){ n.remove(); });
      clone.removeAttribute('transform');
      minimapSvg.appendChild(clone);
      viewportRect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
      viewportRect.setAttribute('class', 'vma-flow__minimap-viewport');
      minimapSvg.appendChild(viewportRect);
      minimap.addEventListener('click', function(e){
        var mr = minimap.getBoundingClientRect();
        var fx = (e.clientX - mr.left) / mr.width;
        var fy = (e.clientY - mr.top) / mr.height;
        var r = rectSize();
        state2.tx = r.w / 2 - (fx * contentW) * state2.scale;
        state2.ty = r.h / 2 - (fy * contentH) * state2.scale;
        apply();
      });
    }
    function updateMinimap() {
      if (!viewportRect) return;
      var r = rectSize();
      var vx = -state2.tx / state2.scale;
      var vy = -state2.ty / state2.scale;
      var vw = r.w / state2.scale;
      var vh = r.h / state2.scale;
      viewportRect.setAttribute('x', vx);
      viewportRect.setAttribute('y', vy);
      viewportRect.setAttribute('width', vw);
      viewportRect.setAttribute('height', vh);
    }

    // Initial render at 100% (no auto-fit) so all diagrams open at a
    // consistent, predictable zoom level regardless of content size. Use the
    // toolbar "fit" button (or the 0 key) to zoom-to-fit on demand.
    apply();

    // Before print: reset to fit so the printed page shows the full diagram
    window.addEventListener('beforeprint', fit);
  }
})();`;
